// ── Perk Definitions ──
// Perks are unlockable content rewards (music, title screens, art, dev videos).
// Unlocked via achievement rewards (perkId in AchievementDefs.ts).
// Content fields (assetKey, url) will be filled when actual assets are available.

export type PerkCategory = 'music' | 'title_screen' | 'art' | 'dev_video';

export interface PerkDef {
  id: string;
  category: PerkCategory;
  name: string;
  description: string;       // short tagline shown on card (keep under ~40 chars)
  hint: string;              // how to unlock (shown on locked cards)
  // Content fields — added later when actual assets are available:
  // assetKey?: string;
  // url?: string;
}

export const PERK_CATEGORY_LABELS: Record<PerkCategory, string> = {
  music: 'Music',
  title_screen: 'Title Screens',
  art: 'Art Gallery',
  dev_video: 'Dev Videos',
};

export const PERK_CATEGORY_ICONS: Record<PerkCategory, string> = {
  music: '🎵',
  title_screen: '🖼️',
  art: '🎨',
  dev_video: '🎬',
};

export const PERK_DEFS: PerkDef[] = [

  // ═══════════════ MUSIC ═══════════════
  { id: 'music_battle_hymn',   category: 'music', name: 'Battle Hymn',   description: 'Epic orchestral combat theme', hint: '50 perfect waves' },
  { id: 'music_speedcore',     category: 'music', name: 'Speedcore Mix', description: 'High-BPM electronic assault', hint: 'Speedrun under 30s' },
  { id: 'music_lofi_beats',    category: 'music', name: 'Lo-Fi Beats',   description: 'Chill beats to idle by', hint: 'Idle on title 5 min' },

  // ═══════════════ TITLE SCREENS ═══════════════
  { id: 'title_lone_wolf',     category: 'title_screen', name: 'Lone Wolf',        description: 'One bot against all odds', hint: 'Win with 1 robot' },
  { id: 'title_lightspeed',    category: 'title_screen', name: 'Lightspeed',       description: 'Warp-speed victory', hint: 'Win under 10 seconds' },
  { id: 'title_hackerman',     category: 'title_screen', name: 'Hackerman',        description: 'Green matrix rain', hint: 'Win with cheats' },
  { id: 'title_completionist', category: 'title_screen', name: 'The Completionist', description: 'Golden prestige title', hint: 'All achievements' },

  // ═══════════════ ART GALLERY ═══════════════
  { id: 'art_platinum_wallpaper',  category: 'art', name: 'Platinum Collection', description: 'Commemorative wallpaper', hint: '10 Platinum ratings' },
  { id: 'art_evolution_chart',     category: 'art', name: 'Evolution Chart',     description: 'Component evolution tree', hint: 'Evolve 25 components' },
  { id: 'art_flying_squadron',     category: 'art', name: 'Flying Squadron',     description: 'Aerial fleet formation', hint: 'Win with flyers only' },
  { id: 'art_deflector_blueprint', category: 'art', name: 'Deflector Blueprint', description: 'Ricochet system schematic', hint: 'Ricochet 500 shots' },
  { id: 'art_targeting_system',    category: 'art', name: 'Targeting System',    description: 'HUD overlay concept art', hint: '95%+ accuracy' },
  { id: 'art_white_flag',          category: 'art', name: 'White Flag',          description: 'Peace was an option', hint: 'Pacifist easter egg' },
  { id: 'art_jenga_tower',         category: 'art', name: 'Tower of Babel',      description: 'Architectural marvel', hint: '8+ tall robot column' },

  // ═══════════════ DEV VIDEOS ═══════════════
  { id: 'video_phoenix_rising', category: 'dev_video', name: 'Phoenix Rising',   description: 'Win after total wipeout', hint: 'Win with 0 bots alive' },
  { id: 'video_killreel',       category: 'dev_video', name: 'Kill Compilation', description: 'Greatest hits montage', hint: 'Destroy 10,000 enemies' },
  { id: 'link_secret_discord',  category: 'dev_video', name: 'Secret Channel',   description: 'Hidden Discord access', hint: 'Extreme persistence' },
];
